/**
 * Comptage des gels de lecture côté spectateur. Logique pure : la balise vidéo, l'horloge et
 * l'envoi de la trace sont pilotés par `VideoHlsView`.
 *
 * Un gel n'est pas une erreur : hls.js réessaie seul, la lecture repart, et rien ne remonte
 * aujourd'hui. C'est pourtant le symptôme dont les clients se plaignent.
 */

/** En dessous, c'est du jitter de décodage imperceptible qui noierait la mesure. */
export const STALL_MIN_DURATION_MS = 1000;

/** Au-delà la charge utile grossit sans rien apprendre. On tronque, mais jamais en silence. */
export const STALL_MAX_RECORDED = 20;

export type StallErrorCategory = 'buffer' | 'fragment' | 'playlist' | 'other';

export type RecordedStall = {
  /** Instant du contenu, comparable d'un spectateur à l'autre. `null` si le player ne l'a pas encore. */
  positionMs: number | null;
  durationMs: number;
  /**
   * Flux sur lequel le gel s'est produit, capturé à son début. Le spectateur peut avoir été
   * déclassé depuis : sans ça, les gels seraient imputés au flux d'arrivée et non au fautif.
   */
  quality: string;
  /** Gel encore en cours quand le spectateur est parti : sa durée s'arrête à ce départ. */
  interrupted?: true;
};

/** En dessous, la position n'a pas bougé : un `timeupdate` à l'arrêt n'est pas une reprise. */
export const STALL_PROGRESS_EPSILON_S = 0.25;

export type StallState = {
  pending: {
    startedAtMs: number;
    positionMs: number | null;
    quality: string;
    /** Position de lecture (`currentTime`) au départ du gel, ou après un saut du lecteur. */
    mediaTimeS: number | null;
  } | null;
  stalls: RecordedStall[];
  omittedStalls: number;
  count: number;
  totalMs: number;
  longestMs: number;
  errors: Record<StallErrorCategory, number>;
  /** Cumul par flux, tenu hors de `stalls` pour survivre à la troncature. */
  byQuality: Record<string, StallsPerQuality>;
};

export type StallsPerQuality = { count: number; totalMs: number };

export type StallSummary = {
  count: number;
  totalMs: number;
  longestMs: number;
  stalls: RecordedStall[];
  omittedStalls: number;
  errors: Record<StallErrorCategory, number>;
  byQuality: Record<string, StallsPerQuality>;
};

export const createStallState = (): StallState => ({
  pending: null,
  stalls: [],
  omittedStalls: 0,
  count: 0,
  totalMs: 0,
  longestMs: 0,
  errors: { buffer: 0, fragment: 0, playlist: 0, other: 0 },
  byQuality: {},
});

/**
 * Le navigateur peut réémettre `waiting` sans `playing` intermédiaire : le premier départ fait foi,
 * sinon chaque répétition raccourcirait le gel mesuré.
 */
export function stallBegan(
  state: StallState,
  startedAtMs: number,
  positionMs: number | null,
  quality: string,
  mediaTimeS: number | null = null,
): StallState {
  if (state.pending !== null) return state;
  return { ...state, pending: { startedAtMs, positionMs, quality, mediaTimeS } };
}

/**
 * Abandon d'un gel en cours sans le comptabiliser : pause volontaire, onglet masqué, déplacement
 * dans la timeline. Sans ça, une pause de dix minutes deviendrait un gel de dix minutes.
 */
export function stallCancelled(state: StallState): StallState {
  if (state.pending === null) return state;
  return { ...state, pending: null };
}

/**
 * Saut de position décidé par le lecteur lui-même, pas par le spectateur : le gel continue, on
 * déplace seulement la référence pour que le `timeupdate` qui suit le saut ne passe pas pour une
 * reprise. hls.js avance ainsi de deux secondes toutes les deux secondes devant un trou.
 */
export function stallSeeked(state: StallState, mediaTimeS: number): StallState {
  if (state.pending === null) return state;
  return { ...state, pending: { ...state.pending, mediaTimeS } };
}

export function stallEnded(
  state: StallState,
  endedAtMs: number,
  mediaTimeS: number | null = null,
): StallState {
  const pending = state.pending;
  if (pending === null) return state;

  // Un `timeupdate` sans avancée réelle est un retardataire ou l'écho d'un saut : rien n'a repris.
  if (
    mediaTimeS !== null &&
    pending.mediaTimeS !== null &&
    mediaTimeS - pending.mediaTimeS < STALL_PROGRESS_EPSILON_S
  ) {
    return state;
  }

  return closeStall(state, pending, endedAtMs - pending.startedAtMs, false);
}

/**
 * Le spectateur quitte la page ou masque l'onglet pendant un gel. Contrairement à une pause, le gel
 * a bien eu lieu, et c'est souvent lui qui pousse à recharger : on le compte jusqu'à cet instant.
 */
export function stallInterrupted(state: StallState, atMs: number): StallState {
  const pending = state.pending;
  if (pending === null) return state;
  return closeStall(state, pending, atMs - pending.startedAtMs, true);
}

function closeStall(
  state: StallState,
  pending: NonNullable<StallState['pending']>,
  durationMs: number,
  interrupted: boolean,
): StallState {
  if (durationMs < STALL_MIN_DURATION_MS) return { ...state, pending: null };

  const recorded = state.stalls.length < STALL_MAX_RECORDED;
  const previousForQuality = state.byQuality[pending.quality] ?? { count: 0, totalMs: 0 };
  return {
    ...state,
    pending: null,
    count: state.count + 1,
    totalMs: state.totalMs + durationMs,
    longestMs: Math.max(state.longestMs, durationMs),
    byQuality: {
      ...state.byQuality,
      [pending.quality]: {
        count: previousForQuality.count + 1,
        totalMs: previousForQuality.totalMs + durationMs,
      },
    },
    stalls: recorded
      ? [
          ...state.stalls,
          {
            positionMs: pending.positionMs,
            durationMs,
            quality: pending.quality,
            ...(interrupted ? { interrupted: true as const } : {}),
          },
        ]
      : state.stalls,
    omittedStalls: recorded ? state.omittedStalls : state.omittedStalls + 1,
  };
}

export function hlsErrorSeen(state: StallState, category: StallErrorCategory): StallState {
  return { ...state, errors: { ...state.errors, [category]: state.errors[category] + 1 } };
}

/** `details` de hls.js, dont les valeurs utiles sont `bufferStalledError`, `fragLoadError`, etc. */
export function categorizeHlsError(details: string | undefined): StallErrorCategory {
  if (details === undefined) return 'other';
  if (details.startsWith('buffer')) return 'buffer';
  if (details.startsWith('frag')) return 'fragment';
  if (details.startsWith('level') || details.startsWith('manifest')) return 'playlist';
  return 'other';
}

/**
 * `null` quand il n'y a rien à signaler : une session saine ne doit rien émettre, sinon on
 * produirait autant d'événements que de spectateurs pour dire que tout allait bien.
 */
export function stallSummary(state: StallState): StallSummary | null {
  if (state.count === 0) return null;
  return {
    count: state.count,
    totalMs: state.totalMs,
    longestMs: state.longestMs,
    stalls: state.stalls,
    omittedStalls: state.omittedStalls,
    errors: state.errors,
    byQuality: state.byQuality,
  };
}
